// material ui
import { Button, Switch, Tooltip } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import SearchIcon from '@material-ui/icons/Search';
import React, { ChangeEvent } from 'react';
// components
import { Link } from 'react-router-dom';

import { EndpointRequestScorecardProgressBar } from '~/src/dash/components/endpoint-request-scorecard-progressbar';
import { ModelChipReadOnly } from '~/src/dash/components/model-chip';
import { ModelDialogDeleteView } from '~/src/dash/components/model-dialog-delete-view';
import { ModernTextField } from '~/src/dash/components/modern-text-field';
import { ButtonV2, Col, Cols, fontSizing, spacing, Text } from '~/src/dash/publicaUI';
// other
import { IStore } from '~/src/dash/reducers/store';
import { IReduxActionProps, ISessionStateProps, IUserStateProps } from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { deleteColor } from '~/src/dash/utils/material-dashboard-react';
import { noopVoid } from '~/src/dash/utils/noop';

// COMPONENT DESCRIPTION: SettingsAppBar is used to create a sticky bar at the top of a settings view
// This component will likely have to be expanded through additional props to include additional buttons
// or toggles that appear on other settings pages. Potentially, we handle this by either passing
// custom render functions or if it is likely to be a commmon feature (like active switch or the name editor)
// we can create renderTypes like in the ListTable component along with props that determine which
// features should be active

// TODO: Use this component in Bidder, Site, Campaign, Report form views

interface IProps extends IReduxActionProps, ISessionStateProps, IUserStateProps {
  name?: string;
  name2?: string;
  isActive: boolean;
  // determines if user has write rights and can modify/delete
  hasWriteRights: boolean;
  // determines if buttons are disabled while API call is made
  isUpdating: boolean;
  // handlers
  handleNameChange?: (name: string) => void;
  handleNameChange2?: (name: string) => void;
  handleSave?: () => void;
  handleClose: () => void;
  cloneUrl: string;
  handleDelete?: () => void;
  handleChangeActive?: () => void;
  handlePreviewXML?: () => void;
  // for campaigns
  progress: string;
  renderStatus?: () => JSX.Element;
  resourceID: string;
  // for endpoint request scorecard
  changeTabView?: (idx: number) => void;
  endpointScorecard?: { [key: string]: number };
  isChannelBulkUpdate?: boolean;
  isChannel?: boolean;
  disableSave?: boolean;
  disableActive?: boolean;
  relative?: boolean;
}

interface IState {
  deleteModalActive: boolean;
  isUpdating: boolean;
}

export const SettingsAppBar = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
  },
  class extends React.Component<IProps, IState> {
    state = {
      deleteModalActive: false,
      isUpdating: false,
    };

    // -------- HANDLER FUNCTIONS -------- //
    handleCloseDialog = (): void => {
      this.setState({
        deleteModalActive: false,
        isUpdating: false,
      });
    };

    handleOpenDeleteModel = (): void => {
      this.setState({
        deleteModalActive: true,
        isUpdating: true,
      });
    };

    handleNameChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
      if (this.props.handleNameChange) {
        this.props.handleNameChange(event.target.value);
      }
    };

    handleNameChange2 = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
      if (this.props.handleNameChange2) {
        this.props.handleNameChange2(event.target.value);
      }
    };

    render() {
      const {
        name,
        name2,
        isActive,
        handleSave,
        handleClose,
        cloneUrl,
        handleDelete,
        handleChangeActive,
        isUpdating,
        hasWriteRights,
        handlePreviewXML,
        progress,
        renderStatus,
        handleNameChange,
        disableSave,
        disableActive,
        relative,
        resourceID,
      } = this.props;
      const { deleteModalActive } = this.state;
      const linkDestination = {
        pathname: `/app/pub/${this.props.session.publisher.ID}/change-logs`,
        state: {
          resourceID,
        },
      };

      return (
        <>
          <Cols
            largeHorizontalPadding
            smallVerticalPadding
            gap={spacing.extraSmall}
            rowBreakSize='medium'
            style={{
              borderTop: '1px solid #cbcbcb',
              borderBottom: '1px solid #cbcbcb',
              position: relative ? 'relative' : 'fixed',
              minHeight: '48px',
            }}
            className='cypress-toolbar'
          >
            {/* COLUMN ONE */}
            <Col flex>
              <Cols
                gap={spacing.medium}
                style={{
                  height: '100%',
                  alignItems: 'center',
                }}
              >
                {/* NAME OR EDIT NAME */}
                {name !== undefined && !this.props.isChannelBulkUpdate && (
                  <Col flex>
                    {hasWriteRights && handleNameChange ? (
                      <ModernTextField value={name} onChange={this.handleNameChange} />
                    ) : (
                      <Text
                        small
                        bold
                        style={{
                          textTransform: 'uppercase',
                        }}
                      >
                        {name}
                      </Text>
                    )}
                  </Col>
                )}
                {/* NAME TWO */}
                {hasWriteRights && name2 && (
                  <Col flex>
                    <ModernTextField
                      value={name2 || ''}
                      onChange={this.handleNameChange2 || noopVoid}
                    />
                  </Col>
                )}
                {/* endpoint request scorecard progress bar */}
                {!this.props.isChannelBulkUpdate && this.props.isChannel && (
                  <Col style={{ marginTop: '2px' }}>
                    <EndpointRequestScorecardProgressBar
                      changeTabView={this.props.changeTabView}
                      totalCompletionPercent={
                        this.props.endpointScorecard
                          ? this.props.endpointScorecard.totalCompletionPercent
                          : 0
                      }
                    />
                  </Col>
                )}
              </Cols>
            </Col>
            {/* COLUMN TWO */}
            <Col flex>
              <Cols
                wrap
                responsive
                style={{
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
                mediumStyle={{ justifyContent: 'space-between' }}
              >
                {/* ROW TWO COLUMN ONE */}
                {/* MISC */}
                <Col paddingRight={spacing.medium} responsive mediumStyle={{ flex: '1 1 0%' }}>
                  {(handleChangeActive || renderStatus) && (
                    <Cols gap={spacing.medium} style={{ alignItems: 'center' }}>
                      {/* Active Switch Section - NOT supported by bulk updating */}
                      {handleChangeActive && !this.props.isChannelBulkUpdate && (
                        <Col>
                          <Cols style={{ alignItems: 'center' }}>
                            <Col>
                              <Switch
                                className={'pb-settings-app-bar-active-toggle'}
                                disabled={!hasWriteRights || !!disableActive}
                                checked={isActive}
                                onChange={handleChangeActive}
                                color='secondary'
                              />
                            </Col>
                            <Col>
                              <Text small>Active</Text>
                            </Col>
                          </Cols>
                        </Col>
                      )}
                      {/* Link to Change Logs */}
                      <Col>
                        <Cols>
                          <Link
                            to={linkDestination}
                            style={{ textDecoration: 'none', fontSize: fontSizing.small }}
                          >
                            Change Logs
                          </Link>
                        </Cols>
                      </Col>
                      {/* Status Section */}
                      {renderStatus && <Col>{renderStatus()}</Col>}
                      {/* Progress Section */}
                      {progress !== '' && (
                        <Col>
                          <Text small>{progress}</Text>
                        </Col>
                      )}
                    </Cols>
                  )}
                </Col>
                {/* ROW TWO COLUMN TWO */}
                {/* BUTTONS */}
                <Col responsive mediumStyle={{ flex: '1 1 0%' }}>
                  <Cols
                    gap={spacing.small}
                    style={{
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }}
                  >
                    {/* SAVE BUTTON */}
                    {handleSave && (
                      <Col>
                        {hasWriteRights ? (
                          <ButtonV2
                            onClick={handleSave}
                            disabled={isUpdating || !!disableSave}
                            allCaps
                            className='cypress-settings-app-bar_button-save'
                          >
                            Save
                          </ButtonV2>
                        ) : (
                          <Tooltip title='Read only access only. Please contact owner to make changes.'>
                            <ModelChipReadOnly label='Read Only' />
                          </Tooltip>
                        )}
                      </Col>
                    )}
                    {/* CLOSE BUTTON */}
                    {handleClose && (
                      <Col>
                        <ButtonV2 clearBackground onClick={handleClose} allCaps>
                          Close
                        </ButtonV2>
                      </Col>
                    )}
                    {/* CLONE BUTTON */}
                    {cloneUrl && hasWriteRights && (
                      <Col>
                        <Tooltip title='Clone'>
                          <Button
                            style={{ color: publicaColors.gray }}
                            component={Link}
                            to={this.props.cloneUrl}
                          >
                            <FileCopyIcon />
                          </Button>
                        </Tooltip>
                      </Col>
                    )}
                    {/* PREVIEW XML BUTTON */}
                    {handlePreviewXML && hasWriteRights && (
                      <Col>
                        <Tooltip title='Preview XML'>
                          <Button style={{ color: publicaColors.gray }} onClick={handlePreviewXML}>
                            <SearchIcon />
                          </Button>
                        </Tooltip>
                      </Col>
                    )}
                    {/* DELETE BUTTON - NOT supported by bulk updating */}
                    {handleDelete && !this.props.isChannelBulkUpdate && hasWriteRights && (
                      <Col>
                        <Tooltip title='Delete'>
                          <Button
                            style={{ color: deleteColor }}
                            onClick={this.handleOpenDeleteModel}
                            disabled={isUpdating}
                          >
                            <DeleteIcon />
                          </Button>
                        </Tooltip>
                      </Col>
                    )}
                  </Cols>
                </Col>
              </Cols>
            </Col>
          </Cols>
          {/* DELETE MODAL */}
          <ModelDialogDeleteView
            active={deleteModalActive}
            model={''}
            ID={''}
            Name={name}
            deleteModel={() => {
              if (handleDelete) {
                return handleDelete();
              }
            }}
            onDelete={this.handleCloseDialog}
            onError={this.handleCloseDialog}
            onClose={this.handleCloseDialog}
          />
        </>
      );
    }
  },
);
